import React, { useState, useEffect } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { useSelector } from 'react-redux';
import moment from 'moment';
import { Box, FlexBox, FormDialog, Grid, Icon, Typography } from '@kis-technologies/kis-ui';
import { useActions } from 'hooks/useActions';
import {
  KioskSalesDetails,
  SalesDetails,
  actions as deliveryLogActions,
  selectors as deliveryLogSelectors
} from 'store/deliveryLog';
import { roundTo2dp } from 'utils/number';
import { useStyles } from '../styled';
import { SettlementReportOverlayProps } from '../types';

export const SettlementReportOverlay = (props: SettlementReportOverlayProps) => {
  const { clientIntegration, downloadContentAsCSV, isGlobalAdmin, onClose, setErrorMessage } = props;

  const classes = useStyles({ isOpen: true })();

  const { getSettlementReport, resetSettlementReport } = useActions({
    getSettlementReport: deliveryLogActions.getSettlementReport,
    resetSettlementReport: deliveryLogActions.resetSettlementReport
  });

  const settlementReport = useSelector(deliveryLogSelectors.selectSettlementReport);

  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);

  const isConfirmDisabled = !startDate || !endDate;

  // ON RENDER
  useEffect(() => {
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
    firstDay.setHours(0, 0, 0, 0);

    setStartDate(firstDay);
    setEndDate(now);
  }, []);

  // ON FETCH SETTLEMENT REPORT SUCCESS
  useEffect(() => {
    if (!settlementReport.data || !settlementReport.total || !startDate || !endDate) {
      return;
    }

    const startDateStr = moment(startDate).format('MM/DD/YYYY');
    const startTimeStr = moment(startDate).format('hh:mm:ss A');
    const endDateStr = moment(endDate).format('MM/DD/YYYY');
    const endTimeStr = moment(endDate).format('hh:mm:ss A');

    // Define columns based on admin role
    const columnConfig = isGlobalAdmin
      ? {
          headers: [
            'Kiosk Name',
            'Venue Name',
            'Area Name',
            'Amount($)',
            'Tax($)',
            'Gross Amount($)',
            'KIS Card Processing Fee Markup($)',
            'KIS Transaction Fee($)',
            'KIS Product Fee($)',
            'KIS Fees($)',
            'Net Amount($)'
          ],
          rowMapper: (row: KioskSalesDetails) => [
            row.kioskName,
            row.venueName,
            row.areaName,
            row.taxesDeducted,
            row.venueTax1,
            row.amount,
            row.cardProcessingFeeMarkup,
            row.transactionFee1,
            row.productFees,
            row.kisFees,
            row.kisFeesDeducted
          ]
        }
      : {
          headers: [
            'Kiosk Name',
            'Venue Name',
            'Area Name',
            'Amount($)',
            'Tax($)',
            'Gross Amount($)',
            'KIS Fees($)',
            'Net Amount($)'
          ],
          rowMapper: (row: KioskSalesDetails) => [
            row.kioskName,
            row.venueName,
            row.areaName,
            row.taxesDeducted,
            row.venueTax1,
            row.amount,
            row.kisFees,
            row.kisFeesDeducted
          ]
        };

    const rows = settlementReport.data.map(columnConfig.rowMapper);

    // Calculate totals
    const totals = settlementReport.data.reduce(
      (acc: SalesDetails, row: KioskSalesDetails) => {
        acc.taxesDeducted += row.taxesDeducted || 0;
        acc.venueTax1 += row.venueTax1 || 0;
        acc.amount += row.amount || 0;
        acc.cardProcessingFeeMarkup! += row.cardProcessingFeeMarkup || 0;
        acc.transactionFee1! += row.transactionFee1 || 0;
        acc.productFees! += row.productFees || 0;
        acc.kisFees += row.kisFees || 0;
        acc.kisFeesDeducted += row.kisFeesDeducted || 0;
        return acc;
      },
      {
        taxesDeducted: 0,
        venueTax1: 0,
        amount: 0,
        cardProcessingFeeMarkup: 0,
        transactionFee1: 0,
        productFees: 0,
        kisFees: 0,
        kisFeesDeducted: 0
      }
    );

    // Build totals row
    const totalsRow = isGlobalAdmin
      ? [
          'Total',
          '',
          '',
          roundTo2dp(totals.taxesDeducted),
          roundTo2dp(totals.venueTax1),
          roundTo2dp(totals.amount),
          roundTo2dp(totals.cardProcessingFeeMarkup as number),
          roundTo2dp(totals.transactionFee1 as number),
          roundTo2dp(totals.productFees as number),
          roundTo2dp(totals.kisFees),
          roundTo2dp(totals.kisFeesDeducted)
        ]
      : [
          'Total',
          '',
          '',
          roundTo2dp(totals.taxesDeducted),
          roundTo2dp(totals.venueTax1),
          roundTo2dp(totals.amount),
          roundTo2dp(totals.kisFees),
          roundTo2dp(totals.kisFeesDeducted)
        ];

    const csvContent = [
      ['KIS Kiosk Settlement Report', '', '', ''],
      ['', '', '', ''],
      ['Start Date', startDateStr, 'Start Time', startTimeStr],
      ['End Date', endDateStr, 'End Time', endTimeStr],
      ['', '', '', ''],
      ['', '', '', ''],
      columnConfig.headers,
      ...rows,
      totalsRow
    ];

    const fileName = `SettlementReport_${clientIntegration.name}_${moment().format('YYYYMMDDHHmmss')}.csv`;

    downloadContentAsCSV(csvContent, fileName);

    resetSettlementReport();
    onClose();
  }, [settlementReport.data, settlementReport.total]);

  // ON FETCH SETTLEMENT REPORT ERROR
  useEffect(() => {
    if (settlementReport.error) {
      setErrorMessage('Uh oh! Something went wrong. Please try again.');
      resetSettlementReport();
    }
  }, [settlementReport.error]);

  /** Validate date range and call getSettlementReport */
  const onConfirm = () => {
    if (startDate && endDate && startDate > endDate) {
      setErrorMessage('Start date must be before end date.');
      return;
    }

    getSettlementReport({
      clientIntegrationId: clientIntegration.id,
      startTime: moment(startDate).utc().format(),
      endTime: moment(endDate).utc().format()
    });
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const DatePickerCustomInput = React.forwardRef((props1: any, ref: any) => {
    return (
      <div
        onClick={props1.onClick}
        ref={ref}
        className={classes.datePickerInput}
        style={{ padding: props1.value ? '0 40px 0 12px' : '0 12px' }}
      >
        <label>{props1.value || props1.placeholder}</label>
        <Icon name="CalendarToday" sx={{ color: '#F46D2E' }} />
      </div>
    );
  });

  return (
    <FormDialog
      cancelButtonAction={onClose}
      cancelButtonText="Cancel"
      confirmButtonAction={onConfirm}
      confirmButtonDisabled={isConfirmDisabled}
      confirmButtonIsLoading={settlementReport.pending}
      confirmButtonText="Build Report"
      open
      title="Settlement Report"
    >
      <Grid direction="column" width="100%" marginTop="10px">
        <FlexBox className={classes.filtersFieldsFlexBox}>
          {/* Start Date */}
          <Box className={classes.filtersFieldBox}>
            <Typography className={classes.filtersFieldLabel}>Start Date and Time</Typography>
            <div className="my-datepicker-wrapper">
              <div className={classes.datePickerContainer}>
                <DatePicker
                  showTimeSelect
                  selected={startDate}
                  onChange={(date) => setStartDate(date)}
                  placeholderText="MM/DD/YYYY hh:mm aa"
                  dateFormat="MM/dd/yyyy h:mm aa"
                  className={classes.datePickerComponent}
                  isClearable
                  customInput={<DatePickerCustomInput />}
                  calendarClassName="my-custom-calendar"
                  popperProps={{ strategy: 'fixed' }}
                />
              </div>
            </div>
          </Box>

          {/* End Date */}
          <Box className={classes.filtersFieldBox}>
            <Typography className={classes.filtersFieldLabel}>End Date and Time</Typography>
            <div className="my-datepicker-wrapper">
              <div className={`${classes.datePickerContainer} ${classes.rightDatePickerContainer}`}>
                <DatePicker
                  showTimeSelect
                  selected={endDate}
                  onChange={(date) => setEndDate(date)}
                  placeholderText="MM/DD/YYYY hh:mm aa"
                  dateFormat="MM/dd/yyyy h:mm aa"
                  className={classes.datePickerComponent}
                  isClearable
                  customInput={<DatePickerCustomInput />}
                  calendarClassName="my-custom-calendar"
                  popperProps={{ strategy: 'fixed' }}
                />
              </div>
            </div>
          </Box>
        </FlexBox>
      </Grid>
    </FormDialog>
  );
};
